import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  CircleAlert,
  Mail,
  RefreshCw,
  Send,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useBulkReachOutCandidateJobMutation } from '@/modules/candidates/mutations/useBulkReachOutCandidateJobMutation';
import { candidateJobQueryKeys } from '@/modules/candidates/queries/queryKeys';
import type {
  CandidateJobApi,
  CandidateJobsPageApi,
} from '@/modules/candidates/types';
import type { CandidateStage } from '@/modules/hiring-manager/hooks/useCandidatePipelineQuery';
import { candidateFullName } from '@/modules/hiring-manager/utils/candidateDisplay';
import type { JobApi } from '@/modules/jobs/types';
import { useResendMessagesMutation } from '@/modules/messaging/mutations/useResendMessagesMutation';
import { Button } from '@/modules/ui/components/button/Button';
import { SendingPausedNotice } from '@/modules/user-external-accounts/components/SendingLimits';
import { useIsLinkedInConnected } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';
import {
  type FailureReason,
  failureReason,
} from '@/pages/hm/messages/outreachThread';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import {
  CandidateTable,
  type CandidateTableGroup,
  type CandidateTableProps,
} from './CandidateTable';
import {
  DRAFTING_TIMEOUT_MS,
  isDraftingDone,
  mergeDraftingBatch,
  readDraftingBatch,
  writeDraftingBatch,
} from './draftingBatch';
import { LinkedInNotice } from './LinkedInNotice';
import { hasDraft, REVIEW_DRAFTS, REVIEW_PARAM } from './outreachDrafts';
import { OutreachDraftsSheet } from './OutreachDraftsSheet';
import {
  ConnectLinkedInBanner,
  GroupNotice,
  SessionExpiredBanner,
} from './OutreachNotices';

/**
 * Stages the customer can draft for: the reach-out worker accepts New and
 * Shortlisted. Sourcing-only jobs never shortlist, so their matches stay New.
 */
const isContactableStage = (stage: CandidateStage) =>
  stage === 'sourced' || stage === 'shortlisted';

/** Why LinkedIn did not take the first message; undefined if it did. */
const failedBecause = (
  candidateJob: CandidateJobApi,
): FailureReason | undefined =>
  candidateJob.reachOutMessage?.status === 'Failed'
    ? failureReason(candidateJob.reachOutMessage)
    : undefined;

/**
 * Whether resending the person's failed message now can help, so it joins
 * the group's Resend: not against the weekly limit, nor an unreachable
 * profile. The API requeues an expired session's messages on reconnect, but
 * only up to 14 days old (finlay-api #710); older ones stay failed, so they
 * are resent like LinkedIn's own errors once LinkedIn is back.
 */
const isResendable = (candidateJob: CandidateJobApi, isConnected: boolean) => {
  const reason = failedBecause(candidateJob);
  return reason === 'unknown' || (reason === 'disconnected' && isConnected);
};

interface ManualOutreachViewProps {
  job: JobApi;
  /** The table's stage groups; this view adds the selection and the outreach actions. */
  groups: CandidateTableGroup[];
  /** Everything else the table needs, passed through. */
  table: Omit<CandidateTableProps, 'groups' | 'selection' | 'rowAction'>;
  /** Where LinkedIn gets connected; absent when this workspace cannot. */
  integrationsHref?: string;
  /** The rows are a search's matches, not the whole pipeline. */
  isSearching: boolean;
  /** Shown instead of the table when a search matches nobody. */
  noMatches?: ReactNode;
}

/**
 * The pipeline table on a job where the customer does the outreach
 * (`outreach: 'me'`). Sourced and Shortlisted rows can be ticked; "Draft messages" asks
 * Finlay to write one per person (`POST /jobs/{jobId}/candidates/reachout`,
 * which is asynchronous). The worker's `OutreachGenerated` notification
 * refreshes the list through `useNotificationHub`. The sheet opens only when
 * the customer asks for it (Review & send, or the notification link), never
 * when drafts arrive, so it does not interrupt whatever they are doing.
 * Drafts are reviewed and sent from `OutreachDraftsSheet`. The worker moves a
 * candidate to ContactStarted when it writes the draft, before anything is
 * sent, so drafted people show under Outreach.
 */
export function ManualOutreachView({
  job,
  groups,
  table,
  integrationsHref,
  isSearching,
  noMatches,
}: ManualOutreachViewProps) {
  const queryClient = useQueryClient();
  const reachOutMutation = useBulkReachOutCandidateJobMutation();
  const resendMutation = useResendMessagesMutation();
  const { linkedInStatus, linkedInAccountId, retryLinkedInLookup } =
    useIsLinkedInConnected();
  const isConnected = linkedInStatus === 'connected';
  // Ticking people is the moment drafting is wanted, so the selection bar
  // offers the connection itself instead of a disabled Draft button.
  const connectHref =
    linkedInStatus === 'disconnected' ? integrationsHref : undefined;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Candidates we asked drafts for, restored after navigation or a reload.
  const [drafting, setDrafting] = useState(() =>
    readDraftingBatch(window.sessionStorage, job.id),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const isSheetOpen = searchParams.get(REVIEW_PARAM) === REVIEW_DRAFTS;

  const candidates = groups.flatMap((group) => group.query.candidates ?? []);
  const byCandidateId = new Map(
    candidates.map((candidateJob) => [candidateJob.candidate.id, candidateJob]),
  );
  const stillDrafting = (drafting?.ids ?? []).filter(
    (id) => !byCandidateId.get(id)?.reachOutMessage,
  );
  const drafts = candidates.filter(hasDraft);
  const isSelectable = (candidateJob: CandidateJobApi) =>
    !stillDrafting.includes(candidateJob.candidate.id);
  const isSelected = (candidateJob: CandidateJobApi) =>
    selectedIds.includes(candidateJob.candidate.id);
  const contactable = groups
    .filter((group) => isContactableStage(group.key))
    .flatMap((group) => group.query.candidates ?? []);
  const selected = contactable.filter(isSelectable).filter(isSelected);
  const outreach = groups.find((group) => group.key === 'outreach');
  const outreachRows = outreach?.query.candidates ?? [];
  const expiredCount = outreachRows.filter(
    (candidateJob) => failedBecause(candidateJob) === 'disconnected',
  ).length;
  // Messages exist only if LinkedIn was connected once, so it has expired
  // (or was disconnected) since: nothing can be sent until it is back.
  const isSessionExpired =
    linkedInStatus === 'disconnected' &&
    outreachRows.some((candidateJob) => candidateJob.reachOutMessage);
  // Reach-out message ids being resent, until the list shows them queued.
  const [resendingIds, setResendingIds] = useState<string[]>([]);

  useEffect(() => {
    writeDraftingBatch(window.sessionStorage, job.id, drafting);
  }, [drafting, job.id]);

  // The notification refreshed the list and every draft exists: stop
  // waiting. The drafts banner then offers the review. A search hides
  // candidates, and a hidden one would count as drafted, so it waits.
  const isEveryStageLoaded = groups.every(
    (group) => group.query.candidates !== undefined,
  );
  if (
    drafting &&
    !isSearching &&
    isEveryStageLoaded &&
    isDraftingDone(drafting, candidates)
  ) {
    setDrafting(null);
  }

  // The worker notifies only when at least one draft was written, so give
  // up after a while: refetch once in case the notification was missed
  // (the hub was reconnecting), then say what went wrong. A callback already
  // awaiting the refetch when a second batch merges in must not act on the
  // stale batch, so the cleanup marks it cancelled.
  useEffect(() => {
    if (!drafting) return;
    let isCancelled = false;
    const timer = window.setTimeout(
      async () => {
        const listKey = candidateJobQueryKeys.lists(job.id);
        await queryClient.invalidateQueries({ queryKey: listKey });
        if (isCancelled) return;
        const rows = queryClient
          .getQueriesData<InfiniteData<CandidateJobsPageApi | undefined>>({
            queryKey: [...listKey, 'infinite'],
          })
          .flatMap(([, data]) =>
            (data?.pages ?? []).flatMap((page) => page?.items ?? []),
          );
        setDrafting(null);
        if (isDraftingDone(drafting, rows)) return;
        toast.error(
          "Couldn't prepare the drafts. Check that LinkedIn is connected, then try again.",
        );
      },
      Math.max(0, drafting.startedAt + DRAFTING_TIMEOUT_MS - Date.now()),
    );
    return () => {
      isCancelled = true;
      window.clearTimeout(timer);
    };
  }, [drafting, queryClient, job.id]);

  const toggle = (candidateId: string) =>
    setSelectedIds((ids) =>
      ids.includes(candidateId)
        ? ids.filter((id) => id !== candidateId)
        : [...ids, candidateId],
    );

  const draftMessages = () => {
    const candidateIds = selected.map(
      (candidateJob) => candidateJob.candidate.id,
    );
    reachOutMutation.mutate(
      { jobId: job.id, candidateIds },
      {
        onSuccess: () => {
          // Merge with any batch still being written so its rows keep showing
          // as drafting until every draft exists.
          setDrafting((current) =>
            mergeDraftingBatch(current, candidateIds, Date.now()),
          );
          // Keep anyone ticked while the request was in flight.
          setSelectedIds((ids) =>
            ids.filter((id) => !candidateIds.includes(id)),
          );
          toast.success(
            `Finlay is writing ${candidateIds.length} ${candidateIds.length === 1 ? 'draft' : 'drafts'}. You get a notification when they are ready to review.`,
          );
        },
        onError: (error) => {
          toast.error(actionErrorMessage('start drafting', error));
        },
      },
    );
  };

  // The chips turning "Queued · sending" are the confirmation, so the
  // buttons stay busy until the refetched list shows them.
  const resend = async (candidateJobs: CandidateJobApi[]) => {
    const messageIds = candidateJobs.flatMap((candidateJob) =>
      candidateJob.reachOutMessage ? [candidateJob.reachOutMessage.id] : [],
    );
    setResendingIds((ids) => [...ids, ...messageIds]);
    try {
      await resendMutation.mutateAsync(messageIds);
      await queryClient.invalidateQueries({
        queryKey: candidateJobQueryKeys.lists(job.id),
      });
    } catch (error) {
      toast.error(actionErrorMessage('resend the messages', error));
    } finally {
      setResendingIds((ids) => ids.filter((id) => !messageIds.includes(id)));
    }
  };
  const isResending = (candidateJob: CandidateJobApi) =>
    resendingIds.includes(candidateJob.reachOutMessage?.id ?? '');

  // Resend sits on the people LinkedIn turned down, including for its weekly
  // limit (the customer picks the moment; the API never retries it). While
  // the session is expired it waits for the reconnect, and expired rows
  // show none at all.
  const hasRowAction = (candidateJob: CandidateJobApi) =>
    failedBecause(candidateJob) === 'limit' ||
    failedBecause(candidateJob) === 'unknown' ||
    isResendable(candidateJob, isConnected);
  const resendButton = (candidateJob: CandidateJobApi) =>
    hasRowAction(candidateJob) && (
      <Button
        aria-label={`Resend to ${candidateFullName(candidateJob)}`}
        className="h-7 rounded-md"
        disabled={!isConnected}
        loading={isResending(candidateJob)}
        onClick={() => void resend([candidateJob])}
        size="sm"
        variant="outline"
      >
        Resend
      </Button>
    );

  // Replace, not push: Back leaves the page instead of reopening the sheet.
  const setIsSheetOpen = (isOpen: boolean) =>
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        if (isOpen) next.set(REVIEW_PARAM, REVIEW_DRAFTS);
        else next.delete(REVIEW_PARAM);
        return next;
      },
      { replace: true },
    );
  const openSheet = () => setIsSheetOpen(true);

  return (
    <>
      <div className="flex flex-col gap-2">
        {isSessionExpired ? (
          <SessionExpiredBanner
            integrationsHref={integrationsHref}
            waitingCount={expiredCount}
          />
        ) : linkedInStatus === 'disconnected' ? (
          <ConnectLinkedInBanner integrationsHref={integrationsHref} />
        ) : (
          <p className="bg-muted text-muted-foreground flex items-start gap-3 rounded-lg px-4 py-3 text-[13px] leading-[18px]">
            <span className="bg-primary mt-1.5 size-1.5 shrink-0 rounded-full" />
            You handle outreach on this job. Tick the people you want to contact
            and Finlay writes a draft for each. Nothing goes out until you send
            it.
          </p>
        )}
        {/* Only an active account has an id, so never beside the expiry banner. */}
        {linkedInAccountId && (
          <SendingPausedNotice accountId={linkedInAccountId} />
        )}
      </div>

      {noMatches ?? (
        <CandidateTable
          {...table}
          groups={groups.map((group) =>
            group.key === 'outreach'
              ? outreachGroup(group, {
                  drafts: drafts.length,
                  isConnected,
                  isSessionExpired,
                  isResending: outreachRows.some(isResending),
                  onReview: openSheet,
                  onResend: (candidateJobs) => void resend(candidateJobs),
                })
              : group,
          )}
          rowAction={outreachRows.some(hasRowAction) ? resendButton : undefined}
          selection={{
            hasCheckbox: (candidateJob) => contactable.includes(candidateJob),
            isSelected,
            isDrafting: (candidateJob) =>
              stillDrafting.includes(candidateJob.candidate.id),
            onToggle: (candidateJob) => toggle(candidateJob.candidate.id),
            onSetSelected: (candidateJobs, select) => {
              const ids = candidateJobs.map(
                (candidateJob) => candidateJob.candidate.id,
              );
              setSelectedIds((current) =>
                select
                  ? [...new Set([...current, ...ids])]
                  : current.filter((id) => !ids.includes(id)),
              );
            },
          }}
        />
      )}

      {selected.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-30 flex flex-col items-center gap-2 px-4">
          {!connectHref && (
            <LinkedInNotice
              className="bg-background pointer-events-auto rounded-md border px-3 py-1.5 text-[13px] shadow-sm"
              integrationsHref={integrationsHref}
              onRetry={retryLinkedInLookup}
              status={linkedInStatus}
            />
          )}
          <div className="bg-foreground text-background pointer-events-auto flex items-center gap-3 rounded-lg py-1.5 pr-1.5 pl-4 shadow-lg">
            <span className="text-[13px] font-medium whitespace-nowrap tabular-nums">
              {selected.length} selected
            </span>
            <button
              aria-label="Clear selection"
              className="text-background/70 hover:text-background inline-flex size-6 cursor-pointer items-center justify-center rounded-md"
              onClick={() => setSelectedIds([])}
              type="button"
            >
              <X className="size-3.5" />
            </button>
            {connectHref ? (
              <>
                <span className="text-background/70 hidden text-[13px] sm:inline">
                  Drafts need your LinkedIn
                </span>
                <Button asChild className="h-8 rounded-md" size="sm">
                  <Link to={connectHref}>
                    {isSessionExpired
                      ? 'Reconnect LinkedIn'
                      : 'Connect LinkedIn'}
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </>
            ) : (
              <Button
                className="bg-background text-foreground hover:bg-background/90 h-8 rounded-md"
                disabled={!isConnected}
                loading={reachOutMutation.isPending}
                onClick={draftMessages}
                size="sm"
              >
                <Send className="size-3.5" />
                Draft {selected.length === 1 ? 'message' : 'messages'}
              </Button>
            )}
          </div>
        </div>
      )}

      <OutreachDraftsSheet
        drafts={drafts}
        integrationsHref={integrationsHref}
        isOpen={isSheetOpen}
        linkedInStatus={linkedInStatus}
        onRetryLinkedIn={retryLinkedInLookup}
        job={job}
        onOpenChange={setIsSheetOpen}
      />
    </>
  );
}

interface OutreachActions {
  drafts: number;
  isConnected: boolean;
  isSessionExpired: boolean;
  isResending: boolean;
  onReview: () => void;
  onResend: (candidateJobs: CandidateJobApi[]) => void;
}

const messages = (count: number) =>
  count === 1 ? '1 message' : `${count} messages`;

/**
 * Outreach, with its group actions under the header: resend what LinkedIn
 * turned down, then review the drafts. While the session is expired the
 * page banner owns the problem, so nothing here can send.
 */
function outreachGroup(
  group: CandidateTableGroup,
  {
    drafts,
    isConnected,
    isSessionExpired,
    isResending,
    onReview,
    onResend,
  }: OutreachActions,
): CandidateTableGroup {
  const members = group.query.candidates ?? [];
  const resendable = members.filter((candidateJob) =>
    isResendable(candidateJob, isConnected),
  );
  const onlyLinkedInErrors = resendable.every(
    (candidateJob) => failedBecause(candidateJob) === 'unknown',
  );
  const limited = members.filter(
    (candidateJob) => failedBecause(candidateJob) === 'limit',
  ).length;
  const wasNot = resendable.length === 1 ? 'wasn’t' : 'weren’t';

  return {
    ...group,
    hint: 'Finlay drafts a first message for each person; you review and send it.',
    note: limited > 0 ? `${limited} hit LinkedIn’s weekly limit` : undefined,
    notices:
      (resendable.length > 0 && isConnected) || drafts > 0 ? (
        <>
          {resendable.length > 0 && isConnected && (
            <GroupNotice
              action={
                <Button
                  className="h-7 rounded-md"
                  loading={isResending}
                  onClick={() => onResend(resendable)}
                  size="sm"
                  variant="outline"
                >
                  Resend {resendable.length}
                  <RefreshCw className="size-3.5" />
                </Button>
              }
              body={
                onlyLinkedInErrors
                  ? 'Usually a temporary error on LinkedIn’s side.'
                  : 'Some went unsent while LinkedIn was signed out. You’re connected again.'
              }
              icon={CircleAlert}
              title={
                onlyLinkedInErrors
                  ? `${messages(resendable.length)} ${wasNot} accepted by LinkedIn`
                  : `${messages(resendable.length)} ${wasNot} sent`
              }
              tone="failed"
            />
          )}
          {drafts > 0 && (
            <GroupNotice
              action={
                <Button
                  className="h-7 rounded-md"
                  disabled={isSessionExpired}
                  onClick={onReview}
                  size="sm"
                >
                  Review & send {drafts}
                  <ArrowRight className="size-3.5" />
                </Button>
              }
              body={
                isSessionExpired
                  ? 'Sending is paused until you reconnect LinkedIn.'
                  : 'Nothing is sent until you approve.'
              }
              icon={Mail}
              title={
                drafts === 1
                  ? '1 draft is ready for you'
                  : `${drafts} drafts are ready for you`
              }
              tone="drafts"
            />
          )}
        </>
      ) : undefined,
  };
}
